import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Check, Copy, Download, Loader2 } from 'lucide-react';
import { TableCard } from '../../TableCard';
import { AudioPlayer } from './AudioPlayer';
import { apiUrl } from '../../../../apiBase';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { clock, speakerName, transcriptText, type TranscriptFull } from './transcriptsApi';

// ONE MEETING, READ BESIDE ITS RECORDING. The speaker labels are the part a model gets
// wrong, so every turn's timestamp plays that moment: hearing it is how somebody checks
// who that was. Naming a speaker renames every turn of theirs at once and is kept on the
// meeting (`names`), never written into the turns — what the model heard stays as heard.

// A colour per speaker, by order of first appearance; past the palette they cycle.
const SPEAKER_CLASS = [
  'border-blue-400 text-blue-700',
  'border-emerald-400 text-emerald-700',
  'border-amber-400 text-amber-700',
  'border-violet-400 text-violet-700',
  'border-rose-400 text-rose-700',
  'border-cyan-400 text-cyan-700',
];

export function TranscriptView({ id, onBack }: { id: number; onBack: () => void }) {
  const { t } = useLanguage();
  const word = t('notice.transcripts.speakerWord');
  const audio = useRef<HTMLAudioElement>(null);
  const [data, setData] = useState<TranscriptFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [now, setNow] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get<TranscriptFull>(`/api/notice/transcripts/${id}`)
      .then(({ data }) => { setData(data); setNames(data.names); })
      .catch(() => setError(t('notice.transcripts.loadError')));
  }, [id, t]);

  const speakers = useMemo(
    () => [...new Set((data?.turns ?? []).map((turn) => turn.speaker))],
    [data],
  );

  // Saved on blur, not per keystroke: a name is typed whole, and the list is read by
  // whoever opens the meeting next.
  const saveNames = async (next: Record<string, string>) => {
    try {
      const { data: saved } = await axios.patch<TranscriptFull>(
        `/api/notice/transcripts/${id}`, { names: next },
      );
      setData(saved);
    } catch {
      setError(t('notice.transcripts.saveError'));
    }
  };

  const playAt = (seconds: number) => {
    const el = audio.current;
    if (!el) return;
    el.currentTime = seconds;
    void el.play();
  };

  const shown = data ? { ...data, names } : null;

  const download = () => {
    if (!shown) return;
    const blob = new Blob([transcriptText(shown, word)], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${shown.title}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copy = async () => {
    if (!shown) return;
    await navigator.clipboard.writeText(transcriptText(shown, word));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const btn = 'inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50';

  return (
    <TableCard title={data?.title ?? '…'} className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button type="button" onClick={onBack} className={btn}>
          <ArrowLeft className="h-4 w-4" />
          {t('notice.transcripts.back')}
        </button>
        <button type="button" onClick={download} disabled={!data} className={btn}>
          <Download className="h-4 w-4" />
          .txt
        </button>
        <button type="button" onClick={() => void copy()} disabled={!data} className={btn}>
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          {t('notice.transcripts.copy')}
        </button>
      </div>

      {error && <p className="text-center text-sm text-rose-600">{error}</p>}

      {!data ? (
        <div className="flex justify-center py-8 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          <AudioPlayer
            src={apiUrl(`/api/notice/transcripts/${id}/audio`)}
            audioRef={audio}
            onTime={setNow}
          />

          <div className="flex flex-col gap-2">
            <p className="text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('notice.transcripts.nameSpeakers')}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {speakers.map((s, i) => (
                <label key={s} className={`flex items-center gap-2 rounded-lg border-l-4 bg-slate-50 px-2 py-1 ${SPEAKER_CLASS[i % SPEAKER_CLASS.length]}`}>
                  <span className="text-xs font-semibold">{speakerName({}, s, word)}</span>
                  <input
                    className="w-32 rounded border border-slate-300 px-2 py-0.5 text-sm text-slate-800 focus:border-blue-500 focus:outline-none"
                    value={names[s] ?? ''}
                    placeholder="…"
                    onChange={(e) => setNames({ ...names, [s]: e.target.value })}
                    onBlur={() => void saveNames(names)}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {data.turns.map((turn, i) => {
              const playing = now >= turn.start && now < turn.end;
              const colour = SPEAKER_CLASS[speakers.indexOf(turn.speaker) % SPEAKER_CLASS.length];
              return (
                <div
                  key={i}
                  className={`rounded-lg border-l-4 px-3 py-2 transition-colors ${colour} ${playing ? 'bg-blue-50' : 'bg-white'}`}
                >
                  <div className="mb-0.5 flex items-center gap-2 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => playAt(turn.start)}
                      className="font-mono text-slate-400 hover:text-blue-600 hover:underline"
                    >
                      {clock(turn.start)}
                    </button>
                    {speakerName(names, turn.speaker, word)}
                  </div>
                  <p className="text-sm leading-relaxed text-slate-800">{turn.text}</p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </TableCard>
  );
}
